import {rootState} from '@mintdata-wsp/common/state/RootState';
import {Box} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/component/Box';
import {Page} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/Page';
import {
    MouseOverRef,
    SurfaceDragContext,
    SurfaceDropContext,
    SurfacePoint
} from '@mintdata-wsp/common/state/ui/visualDesigner/SurfaceEditorState';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {Row} from '@mintdata-wsp/wsp/ui/components/forms/Row';
import {
    VisualComponentPreview,
    VisualComponentPreviewMode
} from '@mintdata-wsp/wsp/ui/views/visualDesigner/surface/panels/outline/components/VisualComponentPreview';

import bind from 'bind-decorator';
import * as classnames from 'classnames/bind';
import {action, observable} from 'mobx';
import {observer} from 'mobx-react';
import * as React from 'react';
import {DraggableCore, DraggableData} from 'react-draggable';

const styles = require('./SurfaceEditorGlass.less');
const cx = classnames.bind(styles);

export interface SurrfaceEditorGlassProps
{
}

export const HALF_OF_VC_ICON_SIZE_PX:number = 80 / 2;

@observer
export class SurfaceEditorGlass extends React.Component<SurrfaceEditorGlassProps, any>
{
    private _glassRef:HTMLElement;
    private _draggableRef:DraggableCore;

    @observable
    private _curPoint:SurfacePoint = null;
    private _curPointCorrection:SurfacePoint = null;

    @bind
    private _onGlassRef(ref:HTMLElement)
    {
        this._glassRef = ref;
    }

    @bind
    private _onDraggableRef(ref:any)
    {
        this._draggableRef = ref;

        const {glass} = rootState.ui.visualDesigner.surfaceEditor;
        const dragContext = glass.dragContext;

        if( dragContext )
        {
            const e = dragContext.mouseEvent;
            (this._draggableRef as any).handleDragStart(e);
        }
    }

    @action.bound
    private _onDrag(e:MouseEvent, data:DraggableData):void
    {
        if( !this._curPoint )
            return;

        const newX = this._curPoint.x + data.deltaX;
        const newY = this._curPoint.y + data.deltaY;

        this._curPoint = new SurfacePoint(newX, newY);
    }

    @action.bound
    private _onDragStop(e:MouseEvent, data:DraggableData):void
    {
        const {surfaceArea, surfaceSelection, workbenchVisibility, surfaceGlass} = controllers.ui;

        const {glass} = rootState.ui.visualDesigner.surfaceEditor;
        const dragContext = glass.dragContext;

        const curPoint = surfaceArea.adjustSurfacePointToPanZoom(this._curPoint);
        const currentLayerPage:Page = surfaceArea.getCurrentLayerPage();

        const pagesPanelWidth:number = workbenchVisibility.getGridLeftColumnWidthPx();

        if( currentLayerPage && this._curPoint.x > pagesPanelWidth )
        {
            const mouseOverRef:MouseOverRef = surfaceArea.getMouseOverRef();

            const dropContext = new SurfaceDropContext(
                currentLayerPage,
                mouseOverRef ? mouseOverRef.box : null,
                mouseOverRef ? mouseOverRef.repeater : null,
                mouseOverRef ? mouseOverRef.hostId : null,
                curPoint,
                dragContext.componentToDrag
            );

            surfaceArea.dropComponent(
                dropContext,
                (box:Box) => surfaceSelection.setSingleBoxSelection(box)
            );
        }

        if( surfaceGlass.isReparentingComponent() )
            surfaceGlass.disableReparenting();

        surfaceGlass.setDragContext(null);
        this._curPoint = null;
        this._curPointCorrection = null;
    }

    private _renderDragItem():JSX.Element
    {
        const {surfaceGlass} = controllers.ui;
        const {glass} = rootState.ui.visualDesigner.surfaceEditor;
        const dragContext = glass.dragContext;

        if( !dragContext )
            return null;

        if( !this._curPoint )
        {
            this._curPoint = this._computeInitialPositionOnGlass(dragContext);

            const glassClientRect:ClientRect = this._glassRef.getBoundingClientRect();

            this._curPointCorrection = new SurfacePoint(glassClientRect.left, glassClientRect.top);
        }

        const style = SurfacePoint.from(
            this._curPoint.x - HALF_OF_VC_ICON_SIZE_PX - this._curPointCorrection.x,
            this._curPoint.y - HALF_OF_VC_ICON_SIZE_PX - this._curPointCorrection.y
        ).toCssStyle();

        return (
            <DraggableCore ref={this._onDraggableRef}
                           onDrag={this._onDrag}
                           onStop={this._onDragStop}
                           enableUserSelectHack={false}
            >
                <div
                    className={cx('draggingComponentPreviewWrapper', {
                        isReparentingComponent: surfaceGlass.isReparentingComponent()
                    })}
                    style={style}
                >
                    <VisualComponentPreview
                        className={cx('draggingComponentPreview')}
                        component={dragContext.componentToDrag}
                        mode={VisualComponentPreviewMode.IconBlock}
                    />
                </div>
            </DraggableCore>
        );
    }

    private _computeInitialPositionOnGlass(dragContext:SurfaceDragContext):SurfacePoint
    {
        const {mouseEvent} = dragContext;

        const x = mouseEvent.clientX > 0 ? mouseEvent.clientX : 0;
        const y = mouseEvent.clientY > 0 ? mouseEvent.clientY : 0;

        return new SurfacePoint(x, y);
    }

    render()
    {
        return (
            <Row aRef={this._onGlassRef} className={cx('surfaceEditorGlass')}>
                {this._renderDragItem()}
            </Row>
        );
    }
}
